// src/components/GlobalSearch.jsx
//
// Búsqueda global (Ctrl+K). Resultados agrupados por tipo:
//   • Tickets       — vía /tickets?q= (server-side, paginado)
//   • Usuarios      — fetch único + filtro client-side
//   • Departamentos — fetch único + filtro client-side
//
// Al hacer click en un usuario abre UserProfileModal in-place (sin navegar).
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import api from '../services/api';
import UserProfileModal from './UserProfileModal';
import { AuthContext } from '../context/AuthContext';
import { nombreVisible } from '../utils/nombreCorto';

const STATUS_LABEL = { open: 'Abierto', in_progress: 'En progreso', closed: 'Cerrado' };
const STATUS_COLOR = { open: '#2563eb', in_progress: '#d97706', closed: '#6b7280' };

function useDebounce(value, delay) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return debounced;
}

// Resalta coincidencias del término en una cadena, devuelve fragmentos React.
function highlight(text, term) {
    if (!text || !term) return text || '';
    const idx = text.toLowerCase().indexOf(term.toLowerCase());
    if (idx < 0) return text;
    return (
        <>
            {text.slice(0, idx)}
            <mark style={{ background: '#fef08a', color: 'inherit', padding: 0, borderRadius: 2 }}>
                {text.slice(idx, idx + term.length)}
            </mark>
            {text.slice(idx + term.length)}
        </>
    );
}

const SECTION_LABEL = {
    fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1,
    color: 'var(--c-text-4)', padding: '10px 18px 4px',
    background: 'var(--c-msg-bg)',
};

export default function GlobalSearch({ open, onClose }) {
    const { user: viewer } = useContext(AuthContext);
    const [query,   setQuery]   = useState('');
    const [tickets, setTickets] = useState([]);
    const [users,   setUsers]   = useState([]);
    const [depts,   setDepts]   = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [allDepts, setAllDepts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [profileUserId, setProfileUserId] = useState(null);
    const inputRef = useRef(null);
    const navigate = useNavigate();

    const debouncedQ = useDebounce(query, 300);

    // Auto-focus + reset al abrir
    useEffect(() => {
        if (open) {
            setQuery('');
            setTickets([]);
            setUsers([]);
            setDepts([]);
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [open]);

    // Fetch one-shot de usuarios y departamentos al abrir (cacheados)
    useEffect(() => {
        if (!open) return;
        if (allUsers.length === 0) {
            // Se cachea ya con el nombre visible para el que busca: así ni el filtro
            // ni la lista exponen el nombre completo de los agentes al solicitante.
            api.get('/users')
                .then(r => setAllUsers((Array.isArray(r.data) ? r.data : [])
                    .map(u => ({ ...u, name: nombreVisible(u.name, viewer?.role, u.role) }))))
                .catch(() => {});
        }
        if (allDepts.length === 0) {
            api.get('/departments').then(r => setAllDepts(Array.isArray(r.data) ? r.data : [])).catch(() => {});
        }
    }, [open, allUsers.length, allDepts.length, viewer?.role]);

    // Búsqueda al cambiar el debounced query
    useEffect(() => {
        if (!open) return;
        const q = debouncedQ.trim();
        if (!q) {
            setTickets([]); setUsers([]); setDepts([]);
            return;
        }
        // Filtros client-side de users/depts
        const lower = q.toLowerCase();
        setUsers(allUsers.filter(u => u.name?.toLowerCase().includes(lower)).slice(0, 5));
        setDepts(allDepts.filter(d => d.name?.toLowerCase().includes(lower)).slice(0, 5));

        // Fetch server-side de tickets
        let alive = true;
        setLoading(true);
        api.get('/tickets', { params: { q, limit: 6 } })
            .then(r => {
                if (!alive) return;
                const arr = Array.isArray(r.data) ? r.data : (r.data.items || []);
                setTickets(arr);
            })
            .catch(() => { if (alive) setTickets([]); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [debouncedQ, open, allUsers, allDepts]);

    // Esc para cerrar
    useEffect(() => {
        if (!open) return;
        const handler = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [open, onClose]);

    if (!open) return null;

    const handleSelectTicket = (t) => {
        navigate(`/tickets/${t._id}`);
        onClose();
    };
    const handleSelectUser = (u) => {
        setProfileUserId(u._id);
    };
    const handleSelectDept = () => {
        navigate('/departments');
        onClose();
    };

    const hasAny = tickets.length > 0 || users.length > 0 || depts.length > 0;
    const showResults = hasAny || loading;

    const rowStyle = {
        padding: '10px 18px',
        cursor: 'pointer',
        borderBottom: '1px solid var(--c-card-header-border)',
        display: 'flex', alignItems: 'center', gap: 10,
        transition: 'background 0.1s',
    };
    const onRowEnter = e => e.currentTarget.style.background = 'var(--c-msg-bg)';
    const onRowLeave = e => e.currentTarget.style.background = 'transparent';

    return (
        <>
            <div
                style={{
                    position: 'fixed', inset: 0, zIndex: 10000,
                    background: 'rgba(0,0,0,.55)',
                    backdropFilter: 'blur(4px)',
                    WebkitBackdropFilter: 'blur(4px)',
                    display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
                    paddingTop: 80,
                }}
                onClick={onClose}
            >
                <div
                    style={{
                        width: '100%', maxWidth: 580,
                        background: 'var(--c-card)',
                        borderRadius: 14,
                        border: '1px solid var(--c-border)',
                        boxShadow: '0 20px 60px rgba(0,0,0,.25)',
                        overflow: 'hidden',
                        margin: '0 16px',
                    }}
                    onClick={e => e.stopPropagation()}
                >
                    {/* Search input */}
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '14px 18px',
                        borderBottom: showResults || query.trim() ? '1px solid var(--c-border)' : 'none',
                    }}>
                        <span style={{ fontSize: 16, color: 'var(--c-text-3)', flexShrink: 0 }}>🔍</span>
                        <input
                            ref={inputRef}
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                            placeholder="Buscar tickets, personas, departamentos…"
                            aria-label="Buscar"
                            style={{
                                flex: 1, border: 'none', outline: 'none',
                                fontSize: 15, background: 'transparent',
                                color: 'var(--c-text-1)',
                            }}
                        />
                        {loading && <Spinner size="sm" animation="border" variant="primary" />}
                        <span
                            onClick={onClose}
                            style={{
                                fontSize: 11, color: 'var(--c-text-4)', cursor: 'pointer',
                                background: 'var(--c-msg-bg)', border: '1px solid var(--c-border)',
                                borderRadius: 4, padding: '2px 6px', flexShrink: 0,
                            }}
                        >
                            Esc
                        </span>
                    </div>

                    {/* Results agrupados */}
                    {hasAny && (
                        <div style={{ maxHeight: 460, overflowY: 'auto' }}>

                            {tickets.length > 0 && (
                                <>
                                    <div style={SECTION_LABEL}>Tickets</div>
                                    {tickets.map(t => (
                                        <div
                                            key={t._id}
                                            onClick={() => handleSelectTicket(t)}
                                            style={rowStyle}
                                            onMouseEnter={onRowEnter}
                                            onMouseLeave={onRowLeave}
                                        >
                                            <span style={{
                                                background: '#4f46e5', color: '#fff',
                                                borderRadius: 5, padding: '2px 7px',
                                                fontSize: 11, fontWeight: 700, flexShrink: 0,
                                            }}>
                                                #{t.folio || '—'}
                                            </span>
                                            <span style={{
                                                flex: 1, fontSize: 13, fontWeight: 600,
                                                color: 'var(--c-text-1)',
                                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            }}>
                                                {highlight(t.title, debouncedQ.trim())}
                                            </span>
                                            <span style={{
                                                fontSize: 11, fontWeight: 600, flexShrink: 0,
                                                color: STATUS_COLOR[t.status] || '#6b7280',
                                            }}>
                                                {STATUS_LABEL[t.status] || t.status}
                                            </span>
                                            {t.department?.name && (
                                                <span style={{
                                                    fontSize: 11, color: 'var(--c-text-4)',
                                                    flexShrink: 0, maxWidth: 100,
                                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                }}>
                                                    {t.department.name}
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                </>
                            )}

                            {users.length > 0 && (
                                <>
                                    <div style={SECTION_LABEL}>Personas</div>
                                    {users.map(u => (
                                        <div
                                            key={u._id}
                                            onClick={() => handleSelectUser(u)}
                                            style={rowStyle}
                                            onMouseEnter={onRowEnter}
                                            onMouseLeave={onRowLeave}
                                        >
                                            <span style={{
                                                width: 26, height: 26, borderRadius: '50%',
                                                background: '#dbeafe', color: '#1e40af',
                                                fontWeight: 700, fontSize: 11,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                flexShrink: 0,
                                            }}>
                                                {u.name?.trim()?.[0]?.toUpperCase() || '?'}
                                            </span>
                                            <span style={{
                                                flex: 1, fontSize: 13, fontWeight: 600,
                                                color: 'var(--c-text-1)',
                                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            }}>
                                                {highlight(u.name, debouncedQ.trim())}
                                            </span>
                                            <span style={{
                                                fontSize: 10, fontWeight: 700,
                                                textTransform: 'uppercase', letterSpacing: 0.5,
                                                color: 'var(--c-text-4)',
                                                flexShrink: 0,
                                            }}>
                                                {u.role}
                                            </span>
                                        </div>
                                    ))}
                                </>
                            )}

                            {depts.length > 0 && (
                                <>
                                    <div style={SECTION_LABEL}>Departamentos</div>
                                    {depts.map(d => (
                                        <div
                                            key={d._id}
                                            onClick={() => handleSelectDept(d)}
                                            style={rowStyle}
                                            onMouseEnter={onRowEnter}
                                            onMouseLeave={onRowLeave}
                                        >
                                            <span style={{ fontSize: 16, flexShrink: 0 }}>📁</span>
                                            <span style={{
                                                flex: 1, fontSize: 13, fontWeight: 600,
                                                color: 'var(--c-text-1)',
                                            }}>
                                                {highlight(d.name, debouncedQ.trim())}
                                            </span>
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>
                    )}

                    {/* Empty state */}
                    {!loading && query.trim() && !hasAny && (
                        <div style={{ padding: '24px 18px', textAlign: 'center', color: 'var(--c-text-4)', fontSize: 13 }}>
                            Sin resultados para "{query}"
                        </div>
                    )}

                    {/* Hint */}
                    {!query.trim() && (
                        <div style={{ padding: '16px 18px', color: 'var(--c-text-4)', fontSize: 12, textAlign: 'center' }}>
                            Escribe para buscar tickets, personas o departamentos · Esc para cerrar
                        </div>
                    )}
                </div>
            </div>

            {/* Modal de perfil al hacer click en una persona */}
            <UserProfileModal
                userId={profileUserId}
                onClose={() => setProfileUserId(null)}
            />
        </>
    );
}
